import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { ArrowLeft, AlertTriangle, User, Calendar, FileText, CheckCircle, AlertCircle, Info } from 'lucide-react';

export default function PayrollExceptionsPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [exceptions, setExceptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [batchId]);

  const fetchData = async () => {
    try {
      const [exceptionsRes, summaryRes, batchRes] = await Promise.all([
        api.get(`/payroll/batches/${batchId}/exceptions`),
        api.get(`/payroll/batches/${batchId}/exceptions/summary`),
        api.get(`/payroll/batches/${batchId}`)
      ]);
      setExceptions(exceptionsRes.data.items || []);
      setSummary(summaryRes.data);
      setBatch(batchRes.data.data);
    } catch (e) {
      notify.error(e, 'PAYROLL_EXCEPTIONS_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'ERROR':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Error</Badge>;
      case 'WARNING':
        return <Badge className="bg-yellow-500"><AlertTriangle className="h-3 w-3 mr-1" />Warning</Badge>;
      case 'INFO':
        return <Badge variant="secondary"><Info className="h-3 w-3 mr-1" />Info</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      'MISSING_CHECKIN': 'bg-orange-500',
      'MISSING_CHECKOUT': 'bg-orange-500',
      'OVERLAPPING_SHIFT': 'bg-red-500',
      'MISSING_PROGRESS_NOTE': 'bg-yellow-500',
      'NON_COMPLIANT_STAFF': 'bg-red-600',
      'INCIDENT_REPORTED': 'bg-purple-500',
      'HOURS_MISMATCH': 'bg-blue-500',
    };
    return <Badge className={colors[type] || 'bg-gray-500'}>{type.replace(/_/g, ' ')}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="payroll-exceptions-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/finance/payroll')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Batches
        </Button>
      </div>

      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Payroll Exceptions</h1>
          <p className="text-muted-foreground">
            Batch {batch?.batch_no} - Issues detected during payroll generation
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Exceptions</p>
                  <p className="text-2xl font-bold">{summary.batch_summary?.total_exceptions || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Exception Types</p>
                  <p className="text-2xl font-bold">{summary.batch_summary?.exception_types || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          {summary.data?.slice(0, 2).map((item, idx) => (
            <Card key={idx}>
              <CardContent className="pt-6">
                <div>
                  <p className="text-sm text-muted-foreground">{item.type.replace(/_/g, ' ')}</p>
                  <p className="text-2xl font-bold">{item.count}</p>
                  <p className="text-xs text-muted-foreground">{item.staff_affected} staff affected</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Exception Types Breakdown */}
      {summary?.data?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Exceptions by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {summary.data.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  {getTypeBadge(item.type)}
                  <span className="font-bold">{item.count}</span>
                  <span className="text-sm text-muted-foreground">({item.staff_affected} staff)</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exceptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Exceptions</CardTitle>
          <CardDescription>{exceptions.length} exception(s) found</CardDescription>
        </CardHeader>
        <CardContent>
          {exceptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium">No Exceptions Found</p>
              <p className="text-sm mt-1">This batch has no issues detected</p>
            </div>
          ) : (
            <Table data-testid="exceptions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptions.map((exc, idx) => (
                  <TableRow key={idx} data-testid={`exception-row-${idx}`}>
                    <TableCell>{getTypeBadge(exc.type)}</TableCell>
                    <TableCell>{getSeverityBadge(exc.severity)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {exc.staff_name || exc.staff_id}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="truncate">{exc.description}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(exc.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
